import { useState } from 'react'

import { ApiError, fehlertext } from '../../api/client'
import { useBuildBook } from '../../hooks/queries'
import { alsApp } from '../../lib/betriebsart'
import { groesse } from '../../lib/format'
import { notiere } from '../../lib/sendeprotokoll'
import { brauchtEigeneGeste, sichereDatei } from '../../lib/sichern'
import { mapImage } from './mapImage'
import { useTexte } from '../../texte'

/**
 * "Als PDF" and "Als EPUB" -- the server builds, the browser supplies the maps.
 *
 * Both buttons wait until the maps are drawn (`ready`), then collect their
 * still images and send them with the job. When the address comes back, the
 * file is saved the same way as a photo (`sichereDatei`): the share sheet on
 * iPhone and iPad, a download everywhere else. **And a link stays** under
 * the buttons -- from use: "der Download von PDF und epub funktioniert nur
 * im Browser, aber nicht in der WebApp. oder ein Link?" In the app started
 * from the home screen a `<a download>` does nothing on iOS; the link opens
 * the file in Safari, where it can be saved.
 *
 * **Two gestures on iPhone and iPad** (1.53.0). Until then the file was
 * handed to the share sheet right after the build -- minutes after the tap
 * that started it, and iOS opens the sheet only inside a tap. It refused
 * silently, the download that followed did nothing in the app, and what
 * remained was the link. Now the build ends with a button "PDF sichern";
 * its own tap fetches and shares (`brauchtEigeneGeste()`). Always the
 * button, not "the sheet when the build was quick enough": a way that works
 * by the clock looks like chance. Elsewhere (Android, a computer) the
 * download still follows the build by itself, and the button loads again.
 *
 * **There is no button that does nothing:** while not ready, it says what
 * is being waited for; if the build fails, the reason is there as text.
 */
export function ExportButtons({
  tripId,
  withUser,
  from,
  to,
  ready,
  waitingText,
}: {
  tripId: number
  /** The person of the private document -- otherwise the book. */
  withUser?: number
  from: string | null
  to: string | null
  ready: boolean
  waitingText: string
}) {
  const t = useTexte().buch
  const build = useBuildBook(tripId)
  /*
   * **Which of the three steps is running** -- from use: "Meldung: wird
   * gebaut, aber auch nach einigen Minuten ist es nicht fertig." One word
   * for three steps said nothing about where it hung; now the button names
   * the step, and the trail under Settings › Diagnose keeps the times.
   */
  const [running, setRunning] = useState<{ format: 'pdf' | 'epub'; step: 'karten' | 'server' | 'holen' } | null>(null)
  const [built, setBuilt] = useState<{ url: string; filename: string; format: 'pdf' | 'epub' } | null>(null)
  /** The second gesture is running -- fetching, then the sheet or the download. */
  const [holt, setHolt] = useState(false)
  /** What became of the file after the download way; the sheet stays silent. */
  const [hinweis, setHinweis] = useState<{ text: string; fehler: boolean } | null>(null)
  const mimeOf = (format: 'pdf' | 'epub'): string => (format === 'pdf' ? 'application/pdf' : 'application/epub+zip')

  /**
   * Fetch and hand over -- from the build (download systems) or from the
   * button (the share sheet's own gesture). Only the download way says
   * something afterwards: after the sheet the person has seen what
   * happened; a message "gespeichert" under a closed sheet would be a claim.
   */
  async function sichern(ziel: { url: string; filename: string; format: 'pdf' | 'epub' }, anlass: 'bau' | 'knopf', since: () => string) {
    setHolt(true)
    setHinweis(null)
    try {
      const way = await sichereDatei(ziel.url, ziel.filename, mimeOf(ziel.format))
      notiere(`Buch ${ziel.format}: Datei ${way} (${anlass}) nach ${since()} s`)
      if (way === 'geladen') setHinweis({ text: t.dateiGeladen, fehler: false })
    } catch (error) {
      // The link below stays: whoever cannot save here opens the file there.
      notiere(`Buch ${ziel.format}: Datei nicht gesichert (${anlass}) nach ${since()} s: ${error instanceof Error ? error.message : String(error)}`)
      setHinweis({ text: t.dateiNichtGeholt, fehler: true })
    } finally {
      setHolt(false)
    }
  }

  async function maps(): Promise<{ route?: string; points: Record<string, string> }> {
    const route = document.querySelector<HTMLElement>('.buch__karte')
    const points: Record<string, string> = {}
    for (const box of Array.from(document.querySelectorAll<HTMLElement>('.buch__punktkarte[data-punkt]'))) {
      const id = box.dataset.punkt ?? ''
      const image = await mapImage(box)
      if (id !== '' && image !== null) points[id] = image
    }
    const routeImage = route === null ? null : await mapImage(route)
    return routeImage === null ? { points } : { route: routeImage, points }
  }

  async function start(format: 'pdf' | 'epub') {
    const started = performance.now()
    const since = () => ((performance.now() - started) / 1000).toFixed(1)
    setRunning({ format, step: 'karten' })
    try {
      const images = withUser === undefined ? await maps() : { points: {} }
      const size = Object.values(images.points).reduce((sum, i) => sum + i.length, images.route?.length ?? 0)
      notiere(`Buch ${format}: Karten abgemalt in ${since()} s (${Object.keys(images.points).length} Punkte, ${groesse(size)})`)
      setRunning({ format, step: 'server' })
      const result = await build.mutateAsync({
        format,
        with: withUser,
        from: from ?? '',
        to: to ?? '',
        maps: images,
      })
      notiere(`Buch ${format}: Server fertig nach ${since()} s (dort ${result.seconds} s, ${groesse(result.bytes)})`)
      const url = `${result.url}?name=${encodeURIComponent(result.filename)}`
      const ziel = { url, filename: result.filename, format }
      setBuilt(ziel)
      setHinweis(null)
      if (brauchtEigeneGeste()) {
        // The share sheet wants a tap of its own -- the button below is it.
        notiere(`Buch ${format}: wartet auf den Tipp „sichern"`)
      } else {
        setRunning({ format, step: 'holen' })
        await sichern(ziel, 'bau', since)
      }
    } catch (error) {
      // The error is shown below as text -- `build.error`.
      notiere(`Buch ${format}: Fehler nach ${since()} s: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setRunning(null)
    }
  }

  const stepText = (step: 'karten' | 'server' | 'holen'): string =>
    step === 'karten' ? t.exportKarten : step === 'server' ? t.exportServer : t.exportHolen
  const errorText = (error: unknown): string =>
    error instanceof ApiError && error.timedOut ? t.exportKeineAntwort : fehlertext(error)

  return (
    <span className="exportknoepfe">
      {build.isError && (
        <span className="notice notice--knapp" role="alert">{errorText(build.error)}</span>
      )}
      {!ready && <span className="exportknoepfe__hinweis">{waitingText}</span>}
      {built !== null && (
        <>
          <button
            type="button"
            className="button button--small"
            disabled={holt || running !== null}
            data-export="sichern"
            onClick={() => void sichern(built, 'knopf', () => '0.0')}
          >
            {holt ? t.exportHolen : t.dateiSichern(built.format)}
          </button>
          {hinweis !== null && (
            <span className="exportknoepfe__hinweis" data-export="hinweis" {...(hinweis.fehler ? { role: 'alert' } : {})}>
              {hinweis.text}
            </span>
          )}
          <span className="exportknoepfe__hinweis">
            <a href={built.url} target="_blank" rel="noopener" data-export="link">
              {t.dateiOeffnen(built.filename)}
            </a>
            {alsApp() && ` ${t.appHinweis}`}
          </span>
        </>
      )}
      <button
        type="button"
        className="button button--small"
        disabled={!ready || running !== null}
        data-export="pdf"
        onClick={() => void start('pdf')}
      >
        {running?.format === 'pdf' ? stepText(running.step) : t.alsPdf}
      </button>
      <button
        type="button"
        className="button button--small button--outline"
        disabled={!ready || running !== null}
        data-export="epub"
        onClick={() => void start('epub')}
      >
        {running?.format === 'epub' ? stepText(running.step) : t.alsEpub}
      </button>
    </span>
  )
}
